import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, Users, ListChecks, TrendingUp, CalendarClock, Sparkles } from "lucide-react";
import api, { formatINR } from "@/lib/api";
import ProfileStatusBanner from "@/components/ProfileStatusBanner";
import LeaderboardCard from "@/components/LeaderboardCard";
import TodayActions from "@/components/dashboard/TodayActions";
import DeepAnalytics from "@/components/dashboard/DeepAnalytics";
import ShareHub from "@/components/dashboard/ShareHub";
import RankCard from "@/components/dashboard/RankCard";
import { useSettings } from "@/context/SettingsContext";

const STATUS_COLOR = {
  Submitted: "bg-stone-200 text-stone-700",
  Verification: "bg-amber-100 text-amber-800",
  Approved: "bg-blue-100 text-blue-800",
  Rejected: "bg-red-100 text-red-700",
  Paid: "bg-emerald-100 text-emerald-800",
};

export default function Dashboard() {
  const { level_names, partner_label } = useSettings();
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [shareStats, setShareStats] = useState(null);
  const [rank, setRank] = useState(null);
  const [c360, setC360] = useState(null);
  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/customer360/summary").then((r) => setC360(r.data)).catch(() => {});
    api.get("/dashboard/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/share/stats").then((r) => setShareStats(r.data)).catch(() => {});
    api.get("/my-rank").then((r) => setRank(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-stone-500">Loading...</div>;

  const landingUrl = data.referral_code ? `${window.location.origin}/r/${data.referral_code}` : "";
  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
  const conversion = data.total_leads > 0 ? Math.round((data.approved_leads / data.total_leads) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">{today}</span>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-2">Welcome back, {(data.name || "").split(" ")[0] || "there"}</h1>
        </div>
        <Link to="/app/products" data-testid="dash-new-lead" className="rounded-full bg-saffron-500 hover:bg-saffron-400 text-emerald-950 font-black px-5 py-2.5 text-sm shadow-[0_10px_30px_-10px_rgba(251,191,36,.85)] transition-transform hover:-translate-y-0.5">+ New lead</Link>
      </div>

      <ProfileStatusBanner />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Link to="/app/withdraw" data-testid="kpi-wallet" className="col-span-2 lg:col-span-3 bento-dark rounded-3xl p-6 lg:p-7 glow-saffron block">
          <div className="relative z-10 flex items-start justify-between">
            <div><div className="text-[11px] font-bold uppercase tracking-[0.2em] text-saffron-300">Wallet balance</div><div className="font-display text-4xl lg:text-5xl font-black mt-2">{formatINR(data.wallet_balance)}</div></div>
            <span className="w-11 h-11 rounded-2xl bg-white/10 grid place-items-center"><Wallet className="w-5 h-5 text-saffron-300" /></span>
          </div>
          <div className="relative z-10 mt-6 flex flex-wrap items-center gap-3"><span className="rounded-full bg-saffron-500 text-emerald-950 font-black text-xs px-4 py-2">Withdraw →</span><span className="text-xs text-emerald-100/80">Total earned {formatINR(data.total_earned)}</span></div>
        </Link>
        <div data-testid="kpi-earned" className="bg-white rounded-3xl p-5 border border-stone-200 flex flex-col justify-between">
          <span className="w-9 h-9 rounded-xl bg-saffron-100 text-saffron-600 grid place-items-center"><TrendingUp className="w-4 h-4" /></span>
          <div><div className="text-[11px] font-bold uppercase tracking-wider text-stone-500 mt-5">Total earned</div><div className="font-display text-2xl font-black">{formatINR(data.total_earned)}</div></div>
        </div>
        <div data-testid="kpi-team" className="bg-white rounded-3xl p-5 border border-stone-200 flex flex-col justify-between">
          <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-800 grid place-items-center"><Users className="w-4 h-4" /></span>
          <div><div className="text-[11px] font-bold uppercase tracking-wider text-stone-500 mt-5">Team size</div><div className="font-display text-2xl font-black">{data.total_team}</div></div>
        </div>
        <div data-testid="kpi-leads" className="bg-white rounded-3xl p-5 border border-stone-200 flex flex-col justify-between">
          <div className="relative w-12 h-12">
            <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90"><circle cx="18" cy="18" r="15.5" fill="none" stroke="#F4F4F5" strokeWidth="4" /><circle cx="18" cy="18" r="15.5" fill="none" stroke="#FBBF24" strokeWidth="4" strokeDasharray={`${conversion * 0.974} 100`} strokeLinecap="round" /></svg>
            <span className="absolute inset-0 grid place-items-center text-[10px] font-black">{conversion}%</span>
          </div>
          <div><div className="text-[11px] font-bold uppercase tracking-wider text-stone-500 mt-3">Lead conversion</div><div className="text-xs text-stone-500">{data.approved_leads}/{data.total_leads} leads</div></div>
        </div>
      </div>

      <TodayActions actions={analytics?.actions} />

      {c360 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="c360-tiles">
          <Link to="/app/renewals" data-testid="tile-renewals" className="bg-white rounded-3xl p-5 border border-stone-200 flex flex-col justify-between hover:-translate-y-0.5 transition-transform">
            <span className="w-9 h-9 rounded-xl bg-saffron-100 text-saffron-600 grid place-items-center"><CalendarClock className="w-4 h-4" /></span>
            <div><div className="text-[11px] font-bold uppercase tracking-wider text-stone-500 mt-5">Renewals this month</div><div className="font-display text-2xl font-black">{c360.renewals_month}<span className="text-sm text-stone-400 font-bold ml-2">{c360.renewals_week} this week</span></div></div>
          </Link>
          <Link to="/app/clients" data-testid="tile-discovered" className="bg-white rounded-3xl p-5 border border-stone-200 flex flex-col justify-between hover:-translate-y-0.5 transition-transform">
            <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-800 grid place-items-center"><Sparkles className="w-4 h-4" /></span>
            <div><div className="text-[11px] font-bold uppercase tracking-wider text-stone-500 mt-5">Discovered to confirm</div><div className="font-display text-2xl font-black">{c360.discovered_pending}</div></div>
          </Link>
          {c360.unlinked > 0 && (
            <Link to="/app/clients/cleanup" data-testid="tile-unlinked" className="col-span-2 bento-dark rounded-3xl p-5 flex items-center gap-4">
              <div className="relative z-10"><div className="text-[11px] font-bold uppercase tracking-[0.2em] text-saffron-300">Clean-up</div><div className="font-display text-xl font-black mt-1">{c360.unlinked} decodes &amp; vault policies aren't linked to a client</div><div className="text-xs text-emerald-100/80 mt-1">Confirm who they belong to → complete 360 cards and renewals</div></div>
              <span className="relative z-10 ml-auto rounded-full bg-saffron-500 text-emerald-950 font-black text-xs px-4 py-2">Review →</span>
            </Link>
          )}
        </div>
      )}

      <RankCard rank={rank} levelNames={level_names} partnerLabel={partner_label} landingUrl={landingUrl} />

      <DeepAnalytics data={analytics} shareStats={shareStats} />

      <LeaderboardCard />

      <ShareHub data={data} />

      {/* Recent leads */}
      <div className="bg-white rounded-2xl border border-stone-200 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl font-black">Recent Leads</h2>
          <Link to="/app/leads" className="text-sm font-semibold text-emerald-900 hover:text-emerald-950">View all →</Link>
        </div>
        {data.recent_leads.length === 0 ? (
          <div className="text-center py-12 text-stone-500">
            <ListChecks className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No leads yet. <Link to="/app/products" className="text-emerald-900 font-semibold">Submit your first lead →</Link></p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-leads-table">
              <thead>
                <tr className="border-b border-stone-200 text-left text-stone-500 text-xs uppercase tracking-wider">
                  <th className="py-3 pr-4 font-semibold">Customer</th>
                  <th className="py-3 pr-4 font-semibold">Product</th>
                  <th className="py-3 pr-4 font-semibold">Status</th>
                  <th className="py-3 pr-4 font-semibold">Commission</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_leads.map((l) => (
                  <tr key={l.id} className="border-b border-stone-100 last:border-0">
                    <td className="py-4 pr-4 font-semibold">{l.customer_name}</td>
                    <td className="py-4 pr-4 text-stone-700">{l.product_name}</td>
                    <td className="py-4 pr-4">
                      <span data-testid="lead-status-pill" className={`px-2.5 py-1 rounded-full text-xs font-bold ${STATUS_COLOR[l.status]}`}>{l.status}</span>
                    </td>
                    <td className="py-4 pr-4 font-mono">{l.commission_amount ? formatINR(l.commission_amount) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
